import { useCallback, useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Phone } from "lucide-react"
import { toast } from "sonner"

import { adUserByPhone } from "@/actions/ad-user"
import { Button } from "@/components/ui/button"
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Spinner } from "@/components/ui/spinner"
import { adUserByPhoneSchema, type AdUserByPhoneSchema } from "@/schemas/adUserByPhoneSchema"

const AdUserByPhone = () => {
    const [open, setOpen] = useState(false)
    const [loading, setLoading] = useState(false)

    const form = useForm<AdUserByPhoneSchema>({
        resolver: zodResolver(adUserByPhoneSchema),
        mode: "onSubmit",
        reValidateMode: "onChange",
        defaultValues: {
            phoneNumber: "",
        },
    })

    const resetState = useCallback(() => {
        form.reset()
        form.clearErrors()
        setLoading(false)
    }, [form])

    const handleOpenChange = useCallback((nextOpen: boolean) => {
        setOpen(nextOpen)

        if (!nextOpen) {
            resetState()
        }
    }, [resetState])

    const handleSubmit = useCallback(async ({ phoneNumber }: AdUserByPhoneSchema) => {
        if (loading) return

        setLoading(true)

        try {
            const response = await adUserByPhone(phoneNumber)
            const message = response.data.Message || "No se pudo completar la solicitud"

            setOpen(false)
            form.reset()

            if (response.data.Response) {
                toast.success(message)
                return
            }

            toast.error(message)
        } finally {
            setLoading(false)
        }
    }, [form, loading])

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <Button
                type="button"
                className="cursor-pointer h-10 w-full"
                variant="outline"
                onClick={() => {
                    setOpen(true)
                }}
            >
                <Phone className="h-4 w-4" />
                Recuperar
            </Button>

            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>Recuperar usuario por celular</DialogTitle>
                    <DialogDescription>
                        Ingresa el número celular registrado para consultar la respuesta del servicio.
                    </DialogDescription>
                </DialogHeader>

                <Form {...form}>
                    <form className="space-y-4" onSubmit={form.handleSubmit(handleSubmit)}>
                        <FormField
                            control={form.control}
                            name="phoneNumber"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Número celular</FormLabel>
                                    <FormControl>
                                        <Input
                                            {...field}
                                            inputMode="numeric"
                                            maxLength={10}
                                            autoComplete="tel"
                                            placeholder="5512345678"
                                            onChange={(event) => {
                                                const nextValue = event.target.value.replace(/\D/g, "").slice(0, 10)
                                                field.onChange(nextValue)
                                            }}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <DialogFooter>
                            <DialogClose asChild>
                                <Button type="button" variant="outline" disabled={loading}>
                                    Cerrar
                                </Button>
                            </DialogClose>

                            <Button type="submit" className="bg-red-500 hover:bg-red-600" disabled={loading}>
                                {loading ? <Spinner /> : "Consultar"}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    )
}

export default AdUserByPhone
